import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import "./TournamentPromoOverlay.css";

export const TOURNAMENT_REGISTRATION_PATH =
  "/tournaments#captain-registration";
export const TOURNAMENT_DISPLAY_URL =
  "GASTREAMING.LIVE/TOURNAMENTS";

const DEFAULT_TOURNAMENT_ID = "get-active-3v3-75-ovr-2026";
const CREW_HQ_TOURNAMENT_ID = "gas-up-3v3-crew-hq-2026";

function tournamentApiUrl(tournamentId) {
  const base = String(api.base || "/api").replace(/\/$/, "");
  return `${base}/tournaments/${encodeURIComponent(tournamentId || DEFAULT_TOURNAMENT_ID)}`;
}

function useTournamentStatus(tournamentId) {
  const [tournament, setTournament] = useState(null);

  useEffect(() => {
    const controller = new AbortController();

    fetch(tournamentApiUrl(tournamentId), {
      headers: { Accept: "application/json" },
      credentials: "omit",
      signal: controller.signal,
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => setTournament(data?.tournament || null))
      .catch((error) => {
        if (error?.name !== "AbortError") setTournament(null);
      });

    return () => controller.abort();
  }, [tournamentId]);

  return tournament;
}

function usePromoExpansion(mode) {
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (mode === "obs") {
      setExpanded(true);
      return undefined;
    }

    let closeTimer = 0;
    const showPromo = () => {
      setExpanded(true);
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(() => setExpanded(false), 14000);
    };

    showPromo();
    const cycleTimer = window.setInterval(showPromo, 90000);

    return () => {
      window.clearInterval(cycleTimer);
      window.clearTimeout(closeTimer);
    };
  }, [mode]);

  return expanded;
}

export default function TournamentPromoOverlay({
  mode = "embedded",
  clickable = mode !== "obs",
  tournamentId = DEFAULT_TOURNAMENT_ID,
}) {
  const tournament = useTournamentStatus(tournamentId);
  const crewHq = tournamentId === CREW_HQ_TOURNAMENT_ID;
  const expanded = usePromoExpansion(mode);
  const isObs = mode === "obs";
  const registrationOpen = tournament?.registrationOpen !== false;
  const spotsRemaining = Number(tournament?.spotsRemaining);

  const details = useMemo(() => {
    if (!registrationOpen) {
      return "VIEW THE BRACKET, RULES & TOURNAMENT UPDATES";
    }

    const spots =
      Number.isFinite(spotsRemaining) && spotsRemaining > 0
        ? `${spotsRemaining} TEAM SPOTS LEFT • `
        : "";

    return crewHq
      ? `${spots}FREE ENTRY • NO OVR CAP • $300 PRIZE`
      : `${spots}FREE ENTRY • MAX 75 OVR • $300 PRIZE`;
  }, [crewHq, registrationOpen, spotsRemaining]);

  const actionLabel = registrationOpen ? "REGISTER NOW" : "VIEW TOURNAMENT";
  const className = [
    "ga-tourney-promo",
    `ga-tourney-promo--${isObs ? "obs" : "embedded"}`,
    expanded ? "is-expanded" : "is-compact",
    clickable ? "is-clickable" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const content = (
    <>
      <span className="ga-tourney-promo__ball" aria-hidden="true">
        🏀
      </span>
      <span className="ga-tourney-promo__copy">
        <span className="ga-tourney-promo__eyebrow">
          {crewHq ? "NBA 2K27 • CREW HQ 3V3" : "NBA 2K27 PARK 3V3"}
        </span>
        <span className="ga-tourney-promo__details">{details}</span>
        <span className="ga-tourney-promo__url">
          {TOURNAMENT_DISPLAY_URL}
        </span>
      </span>
      <span className="ga-tourney-promo__action">
        {actionLabel}
        {clickable ? <span aria-hidden="true"> →</span> : null}
      </span>
    </>
  );

  return (
    <div
      className={`ga-tourney-promo-stage ga-tourney-promo-stage--${isObs ? "obs" : "embedded"}`}
      aria-live="polite"
    >
      {clickable ? (
        <Link
          to={crewHq ? "/tournaments/gas-up-3v3-crew-headquarters#register" : TOURNAMENT_REGISTRATION_PATH}
          className={className}
          aria-label={crewHq ? "Open G.A.S UP Crew Headquarters tournament" : "Open NBA 2K27 Park 3v3 tournament registration"}
        >
          {content}
        </Link>
      ) : (
        <div className={className} aria-label={crewHq ? "G.A.S UP Crew Headquarters tournament" : "NBA 2K27 Park 3v3 tournament registration"}>
          {content}
        </div>
      )}
    </div>
  );
}
